import { Modal } from '@ui-components/neo.tax';
import { useEffect, useRef, useState } from 'react';

import { GitHubConnectMethod } from '@neo.tax/shared/enums';
import { AccountingProvider, PayrollProvider, TicketingProvider, ProviderType } from '@neo.tax/shared/graphql';

import { ConnectProviders } from '../connect-provider';
import { DeterministicProvider, SelectProviders } from './SelectProviders';

/** Where the wizard starts: which provider, and — for GitHub — how and for which organization. */
type WizardStart = {
  provider: DeterministicProvider | null;
  gitHubConnectMethod: GitHubConnectMethod | null;
  organizationSlug: string | null;
};

const PROVIDER_PICKER: WizardStart = { provider: null, gitHubConnectMethod: null, organizationSlug: null };

const seedFrom = (
  initialProvider: DeterministicProvider | null | undefined,
  initialGitHubConnectMethod: GitHubConnectMethod | null | undefined,
  initialOrganizationSlug: string | null | undefined,
): WizardStart => ({
  provider: initialProvider ?? null,
  gitHubConnectMethod: initialGitHubConnectMethod ?? null,
  organizationSlug: initialOrganizationSlug ?? null,
});

interface ConnectProvidersProps {
  isOpen: boolean;
  close: () => void;
  showProvidersInTypes: Set<ProviderType>;
  disabledAccountingProviders?: AccountingProvider[];
  disabledPayrollProviders?: PayrollProvider[];
  disabledTicketingProviders?: TicketingProvider[];
  fiscalYearId: string;
  // Pre-selects a provider so the modal opens straight to its connect slide (skipping selection).
  // Used to re-open the GitHub App connect flow after its full-page install redirect (PLAT-386).
  initialProvider?: DeterministicProvider | null;
  /**
   * Pre-selects how GitHub is connected, so the modal skips the method-choice step too. Set by the
   * Data Sources connection rows, where the customer chose the method by choosing the action.
   */
  initialGitHubConnectMethod?: GitHubConnectMethod | null;
  /** The organization a resumed or reconnected GitHub App connection is already for. */
  initialOrganizationSlug?: string | null;
}

export const ConnectProvidersModal = ({
  close,
  disabledAccountingProviders,
  disabledPayrollProviders,
  disabledTicketingProviders,
  fiscalYearId,
  initialGitHubConnectMethod,
  initialOrganizationSlug,
  initialProvider,
  isOpen,
  showProvidersInTypes,
}: ConnectProvidersProps): React.ReactElement => {
  const [start, setStart] = useState<WizardStart>(() =>
    seedFrom(initialProvider, initialGitHubConnectMethod, initialOrganizationSlug),
  );

  /**
   * Re-seeded on the closed → open edge, not once at mount and not on every render.
   *
   * Not at mount, because the Data Sources rows open this repeatedly within one page visit and each
   * action starts somewhere different — a second one would otherwise open where the first left off.
   * Not on every render, because the customer navigates within the slide-over and re-seeding would
   * undo that. Remounting the modal to reset it would also work, but it costs the opening
   * transition: headlessui's Transition.Root skips its enter animation when it mounts already
   * shown.
   */
  const wasOpen = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setStart(seedFrom(initialProvider, initialGitHubConnectMethod, initialOrganizationSlug));
    }
    wasOpen.current = isOpen;
  }, [initialGitHubConnectMethod, initialOrganizationSlug, initialProvider, isOpen]);

  // Back to the picker discards the whole start — one object, so the method and the organization
  // cannot be left behind while the provider is cleared. For the organization that is a correctness
  // matter: the install writes whatever is in that field over the connection's own.
  const restartAtProviderPicker = (): void => setStart(PROVIDER_PICKER);

  // Closing leaves the start alone: the slide stays on screen through the closing transition, and
  // the next open re-seeds it anyway.
  const closeProviderModal = close;

  return (
    <Modal isOpen={isOpen}>
      {start.provider ? (
        <ConnectProviders
          fiscalYearId={fiscalYearId}
          close={closeProviderModal}
          restart={restartAtProviderPicker}
          selectedProvider={start.provider}
          initialGitHubConnectMethod={start.gitHubConnectMethod}
          initialOrganizationSlug={start.organizationSlug}
        />
      ) : (
        <SelectProviders
          close={closeProviderModal}
          selectProvider={(provider): void => setStart({ ...PROVIDER_PICKER, provider })}
          showProvidersInTypes={showProvidersInTypes}
          accountingProvidersAlreadyConnected={new Set(disabledAccountingProviders)}
          payrollProvidersAlreadyConnected={new Set(disabledPayrollProviders)}
          ticketingProvidersAlreadyConnected={new Set(disabledTicketingProviders)}
        />
      )}
    </Modal>
  );
};
